import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabase';
import { t } from './translations';
import JarSVG from './JarSVG';

// ─── ONBOARDING FLOW ──────────────────────────────────────────────────────────
// Shown once after signup. Stored in localStorage so it only appears once.
// Steps: Welcome → Set username → Share link → Done

export function shouldShowOnboarding() {
  return !localStorage.getItem('mgm_onboarded');
}

export function markOnboarded() {
  localStorage.setItem('mgm_onboarded', '1');
}

export default function OnboardingFlow({ user, lang, onDone }) {
  const T = t[lang];
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [profile, setProfile] = useState(null);
  const [username, setUsername] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!user) return;
    supabase.from('profiles').select('*').eq('id', user.id).single()
      .then(({ data }) => { if (data) setProfile(data); });
  }, [user]);

  const totalSteps = 4;
  const jarLink = profile ? `myglow.me/${profile.jar_id}` : 'myglow.me/...';
  const fullJarLink = profile ? `https://myglow.me/${profile.jar_id}` : '';

  async function saveUsername() {
    if (!username.trim()) { setStep(s => s + 1); return; }
    if (!/^[a-zA-Z0-9_]{3,20}$/.test(username)) {
      setUsernameError(lang === 'ar' ? 'حروف وأرقام وشرطة سفلية (٣-٢٠ حرف)' : 'Letters, numbers, underscores (3-20 chars)');
      return;
    }
    setSaving(true); setUsernameError('');
    const { error } = await supabase.from('profiles')
      .update({ username: username.toLowerCase() })
      .eq('id', user.id);
    if (error?.message?.includes('unique')) {
      setUsernameError(lang === 'ar' ? 'اسم المستخدم ده مأخود' : 'Username taken, try another');
    } else {
      setStep(s => s + 1);
    }
    setSaving(false);
  }

  function copyLink() {
    if (!fullJarLink) return;
    navigator.clipboard.writeText(fullJarLink).catch(() => {});
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  }

  function finish() {
    markOnboarded();
    onDone();
  }

  const steps = [
    // ── Step 0: Welcome ──
    <div key="welcome" style={{ textAlign: 'center', animation: 'fadeUp 0.4s ease' }}>
      <div style={{ marginBottom: 28 }}>
        <JarSVG fill={0.5} size={120} />
      </div>
      <h2 style={{ fontFamily: T.displayFont, fontSize: 'clamp(1.5rem,4vw,2rem)', marginBottom: 12, background: 'linear-gradient(135deg,#FFE580,#FFD166)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
        {lang === 'ar' ? `أهلاً ${profile?.name || ''} 🎉` : `Welcome, ${profile?.name || ''} 🎉`}
      </h2>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', lineHeight: 1.75, maxWidth: 380, margin: '0 auto 28px' }}>
        {lang === 'ar'
          ? 'جرّتك الخاصة اتعملت! دلوقتي الناس تقدر تبعتلك رسايل أضواء مجهولة تفتحها لما تحتاج دفا.'
          : 'Your personal jar is ready. People can now drop anonymous glows for you to open whenever you need a lift.'}
      </p>
      <button className="btn btn-glow" style={{ padding: '14px 36px', fontSize: '1rem' }} onClick={() => setStep(1)}>
        {lang === 'ar' ? 'خليني أبدأ →' : "Let's set it up →"}
      </button>
    </div>,

    // ── Step 1: Username ──
    <div key="username" style={{ animation: 'fadeUp 0.4s ease' }}>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>🔖</div>
        <h2 style={{ fontFamily: T.displayFont, fontSize: '1.5rem', marginBottom: 8 }}>
          {lang === 'ar' ? 'اختار اسم المستخدم بتاعك' : 'Pick your username'}
        </h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.87rem' }}>
          {lang === 'ar' ? 'هيظهر على بروفايلك العام (اختياري، تقدر تعمله بعدين)' : 'Shows on your public profile — optional, you can do this later'}
        </p>
      </div>
      <div className="form-group">
        <label className="form-label">@username</label>
        <input
          type="text"
          placeholder={lang === 'ar' ? 'مثلاً: sara_glow' : 'e.g. sara_glow'}
          value={username}
          onChange={e => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
          maxLength={20}
          style={{ textAlign: 'left', direction: 'ltr', fontSize: '1.05rem' }}
          autoFocus
        />
        {usernameError && <div className="form-error">{usernameError}</div>}
      </div>
      <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
        <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setStep(2)}>
          {lang === 'ar' ? 'تخطي' : 'Skip for now'}
        </button>
        <button className="btn btn-glow" style={{ flex: 2 }} disabled={saving} onClick={saveUsername}>
          {saving ? '…' : (lang === 'ar' ? 'حفظ ومتابعة' : 'Save & continue')}
        </button>
      </div>
    </div>,

    // ── Step 2: Share link ──
    <div key="share" style={{ animation: 'fadeUp 0.4s ease' }}>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>🔗</div>
        <h2 style={{ fontFamily: T.displayFont, fontSize: '1.5rem', marginBottom: 8 }}>
          {lang === 'ar' ? 'شارك اللينك بتاعك' : 'Share your jar link'}
        </h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.87rem', lineHeight: 1.65 }}>
          {lang === 'ar'
            ? 'هذا هو لينك جرّتك — أي حد يضغط عليه يقدر يبعتلك ضو مجهول'
            : 'Anyone who visits this link can drop you an anonymous glow'}
        </p>
      </div>

      <div style={{ background: 'var(--bg3)', border: '1px solid rgba(255,209,102,0.22)', borderRadius: 14, padding: '18px 20px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 4 }}>
            {lang === 'ar' ? 'لينك جرّتك' : 'Your jar link'}
          </div>
          <div style={{ fontStyle: 'italic', color: 'var(--gold3)', fontSize: '0.9rem' }}>{jarLink}</div>
        </div>
        <button
          onClick={copyLink}
          style={{ flexShrink: 0, padding: '8px 16px', borderRadius: 9, background: copied ? 'rgba(45,212,191,0.18)' : 'rgba(255,209,102,0.12)', color: copied ? 'var(--teal)' : 'var(--gold)', border: `1px solid ${copied ? 'rgba(45,212,191,0.3)' : 'rgba(255,209,102,0.26)'}`, fontSize: '0.82rem', fontFamily: 'inherit', fontWeight: 500, cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap' }}
        >
          {copied ? (lang === 'ar' ? 'اتنسخ ✓' : 'Copied ✓') : (lang === 'ar' ? 'نسخ اللينك' : 'Copy link')}
        </button>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
        {[
          { label: lang === 'ar' ? 'واتساب' : 'WhatsApp', emoji: '💬', url: `https://wa.me/?text=${encodeURIComponent(`${lang === 'ar' ? 'ابعتلي ضو على جرّتي 🫙' : 'Drop me an anonymous glow'} → ${fullJarLink}`)}` },
          { label: lang === 'ar' ? 'انستجرام' : 'Instagram', emoji: '📸', url: 'https://instagram.com' },
        ].map(s => (
          <a key={s.label} href={s.url} target="_blank" rel="noopener noreferrer"
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px', borderRadius: 10, background: 'var(--bg3)', border: '1px solid var(--border)', color: 'var(--text-muted)', fontSize: '0.83rem', textDecoration: 'none', transition: 'all 0.2s' }}>
            {s.emoji} {s.label}
          </a>
        ))}
      </div>

      <button className="btn btn-glow btn-full" style={{ marginTop: 4 }} onClick={() => setStep(3)}>
        {lang === 'ar' ? 'تمام، جاهز →' : 'Done, continue →'}
      </button>
    </div>,

    // ── Step 3: Done ──
    <div key="done" style={{ textAlign: 'center', animation: 'fadeUp 0.4s ease' }}>
      <div style={{ fontSize: '3.5rem', marginBottom: 20, filter: 'drop-shadow(0 0 18px rgba(255,209,102,0.6))' }}>✨</div>
      <h2 style={{ fontFamily: T.displayFont, fontSize: 'clamp(1.4rem,4vw,1.9rem)', marginBottom: 12, background: 'linear-gradient(135deg,#FFE580,#FFD166)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
        {lang === 'ar' ? 'جرّتك جاهزة! 🫙' : 'Your jar is ready! 🫙'}
      </h2>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.75, maxWidth: 360, margin: '0 auto 28px' }}>
        {lang === 'ar'
          ? 'شارك اللينك بتاعك وابدأ تجمع أضواء. افتح جرّتك أي وقت تحتاج دفا.'
          : 'Share your link and start collecting glows. Open your jar whenever you need a lift.'}
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <button className="btn btn-glow" style={{ padding: '14px', fontSize: '0.95rem' }} onClick={finish}>
          {lang === 'ar' ? 'افتح جرّتي ✨' : 'Open my jar ✨'}
        </button>
        <button className="btn btn-ghost" style={{ fontSize: '0.85rem' }} onClick={() => { finish(); setTimeout(() => navigate('/profile'), 100); }}>
          {lang === 'ar' ? 'تعديل البروفايل أولاً' : 'Set up my profile first'}
        </button>
      </div>
    </div>,
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(13,10,20,0.96)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: 'var(--card)', border: '1px solid rgba(255,209,102,0.22)', borderRadius: 24, padding: '36px 32px', maxWidth: 480, width: '100%', boxShadow: '0 24px 80px rgba(0,0,0,0.8), 0 0 60px rgba(255,209,102,0.08)' }} dir={T.dir}>

        {/* Progress dots */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 32 }}>
          {Array.from({ length: totalSteps }, (_, i) => (
            <div key={i} style={{ width: i === step ? 22 : 7, height: 7, borderRadius: 100, background: i === step ? 'var(--gold)' : i < step ? 'rgba(255,209,102,0.4)' : 'var(--bg3)', transition: 'all 0.3s ease' }} />
          ))}
        </div>

        {steps[step]}
      </div>
    </div>
  );
}
